import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams, useParams } from "react-router-dom";
import { useLogin } from "@/hooks/useLogin";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown, Loader2, X } from "lucide-react";
import { AuthLayout, AuthNotice, PanelBlock, APP_NAME } from "@/components/auth/AuthLayout";
import { LATEST_RELEASE } from "@/lib/whatsNew";
import { getClinicBrand } from "@/lib/api";
import { clinicLogoUrl } from "@/lib/brandingApi";
import { ClinicBrand, getRememberedBrand, rememberBrand, getRememberedEmail, rememberEmail } from "@/lib/clinicBrand";

// The panel carries the brand; what's useful at sign-in sits next to the control it's about —
// the shared-computer hint under "Keep me signed in", what's new under the form.
function LoginPanel() {
  return (
    <PanelBlock title={<>Every patient,<br />verified.</>}>
      <p>Queue, prescriptions, billing and IPD — one system for your clinic.</p>
    </PanelBlock>
  );
}

/** "New in 1.2" — collapsed by default; the full list stays under ? once signed in */
function WhatsNewLine() {
  const release = LATEST_RELEASE;
  if (!release?.items.length) return null;
  return (
    <details className="group text-left rounded-lg border border-border px-3 py-2">
      <summary className="flex items-center justify-between gap-2 cursor-pointer list-none touch:min-h-10 text-sm">
        <span>
          <span className="font-medium text-foreground">New in version {release.version}</span>
          <span className="text-muted-foreground"> · {release.items.length} update{release.items.length !== 1 ? "s" : ""}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <ul className="mt-2 mb-1 space-y-1.5 text-sm text-muted-foreground">
        {release.items.map(item => (
          <li key={item.title || item.text}>
            {item.title && <span className="font-medium text-foreground">{item.title}. </span>}
            {item.text}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** A clinic's own sign-in page: the clinic's name is the headline */
function ClinicPanel({ name }: { name: string }) {
  return (
    <PanelBlock eyebrow="Staff sign-in" title={<span className="break-words">{name}</span>}>
      <p>Queue, prescriptions, billing and IPD for the {name} team.</p>
    </PanelBlock>
  );
}

export default function LoginPage() {
  const { login, loading, error, setError } = useLogin();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const sessionExpired = searchParams.get('reason') === 'expired';
  // After a session expired, return to the page the user was on — only paths inside this app
  // ("/x", not "//host", "/\host" or "https://…"), and never back to the sign-in page itself.
  const nextParam = searchParams.get('next') || '';
  const returnTo = /^\/(?![/\\])/.test(nextParam) && !nextParam.startsWith('/login') ? nextParam : null;

  // Someone who kept themselves signed in last time starts with their email filled in and the box ticked
  const [savedEmail, setSavedEmail]   = useState(getRememberedEmail);
  const [email, setEmail]             = useState(savedEmail);
  const [password, setPassword]       = useState("");
  const [rememberMe, setRememberMe]   = useState(!!savedEmail);
  const [submitting, setSubmitting]   = useState(false);
  // From the clinic's link (/c/:slug) once it loads, else the clinic last signed in on this device
  const [brand, setBrand]             = useState<ClinicBrand | null>(() => {
    const known = getRememberedBrand();
    return !slug || known?.slug === slug ? known : null;
  });
  // A clinic link this device doesn't know yet shows nothing until its name loads, never OneCheckUp first
  const [brandPending, setBrandPending] = useState(() => !!slug && getRememberedBrand()?.slug !== slug);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    getClinicBrand(slug)
      .then(b => { if (alive && b) { setBrand(b); rememberBrand(b); } })
      // No such clinic: the plain sign-in, or the one this device knows
      .catch(() => { if (alive) setBrand(getRememberedBrand()); })
      .finally(() => { if (alive) setBrandPending(false); });
    return () => { alive = false; };
  }, [slug]);

  // A different person at this computer: start from an empty email
  const forgetEmail = () => {
    rememberEmail(null);
    setSavedEmail("");
    setEmail("");
    setRememberMe(false);
    emailRef.current?.focus();
  };

  const forgetBrand = () => {
    rememberBrand(null);
    setBrand(null);
    // Someone from another clinic: the remembered email isn't theirs either
    rememberEmail(null);
    setSavedEmail("");
    setEmail("");
    setRememberMe(false);
    if (slug) navigate("/login", { replace: true });
  };

  const getRoleRoute = (user: typeof currentUser): string => {
    if (!user) return "/login";
    if (user.must_change_password) return "/change-password";
    if (user.doctor_id) return "/";
    if (user.role === "staff") return "/frontdesk";
    if (user.role === "admin") return "/settings";
    return "/";
  };

  useEffect(() => {
    const prev = document.title;
    document.title = brand ? `Sign in · ${brand.name} | ${APP_NAME}` : `Sign in | ${APP_NAME}`;
    return () => { document.title = prev; };
  }, [brand]);

  // Email already filled in from last time: start in the password
  useEffect(() => { (email ? passwordRef : emailRef).current?.focus(); }, [brandPending]); // eslint-disable-line react-hooks/exhaustive-deps
  // A failed sign-in is nearly always a mistyped password: put the cursor there, selected, to retype
  useEffect(() => { if (error) { passwordRef.current?.focus(); passwordRef.current?.select(); } }, [error]);
  useEffect(() => {
    if (currentUser) navigate(returnTo ?? getRoleRoute(currentUser), { replace: true });
  }, [currentUser, navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Enter with a box still empty moves to it instead of doing nothing
    if (!email.trim()) { emailRef.current?.focus(); return; }
    if (!password) { passwordRef.current?.focus(); return; }
    setSubmitting(true);
    // Phones and autofill often add a trailing space to the email
    try {
      await login(email.trim(), password, rememberMe);
      rememberEmail(rememberMe ? email.trim() : null);
    }
    catch (err) { console.error("Login failed:", err); }
    finally { setSubmitting(false); }
  };

  if (currentUser || brandPending) return null;

  const busy   = loading || submitting;
  const hasErr = !!error;
  const errId  = "login-error";

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use the email and password your clinic gave you."
      clinic={brand?.name}
      clinicLogo={brand?.slug && brand.logo ? clinicLogoUrl(brand.slug, brand.logo) : undefined}
      panel={brand ? <ClinicPanel name={brand.name} /> : <LoginPanel />}
      footer={
        <>
          <WhatsNewLine />
          {brand ? (
            // Staff of another clinic on this device, or someone who opened the wrong link
            <p>
              Not with {brand.name}?{" "}
              <button type="button" onClick={forgetBrand} className="touch-target font-medium text-primary hover:underline">
                Use the general sign-in
              </button>
            </p>
          ) : (
            <p>
              Not on {APP_NAME} yet?{" "}
              <Link to="/signup" className="touch-target font-medium text-primary hover:underline">
                Get in touch
              </Link>
            </p>
          )}
          <p className="text-xs">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-foreground">Terms</Link> and{" "}
            <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
          </p>
        </>
      }
    >
      {sessionExpired && (
        <div className="mb-5">
          <AuthNotice tone="warning">Your session has expired. Please sign in to continue.</AuthNotice>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="email">Email</Label>
            {savedEmail && email === savedEmail && (
              <button type="button" onClick={forgetEmail} tabIndex={-1} className="touch-target text-xs font-medium text-primary hover:underline">
                Not you?
              </button>
            )}
          </div>
          <Input
            ref={emailRef}
            id="email"
            type="email"
            placeholder="you@clinic.com"
            value={email}
            onChange={e => { setEmail(e.target.value); if (error) setError(null); }}
            disabled={busy}
            required
            autoComplete="email"
            aria-describedby={hasErr ? errId : undefined}
            aria-invalid={hasErr}
            className="h-11"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            ref={passwordRef}
            id="password"
            value={password}
            onChange={e => { setPassword(e.target.value); if (error) setError(null); }}
            disabled={busy}
            required
            autoComplete="current-password"
            aria-describedby={hasErr ? errId : undefined}
            aria-invalid={hasErr}
            className="h-11"
          />
        </div>

        {/* After the password in the tab order, so Tab goes email → password */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <label htmlFor="remember" className="inline-flex items-center gap-2 touch:min-h-10 text-sm text-foreground cursor-pointer select-none">
              <Checkbox id="remember" checked={rememberMe} onCheckedChange={v => setRememberMe(v === true)} disabled={busy} aria-describedby="remember-hint" />
              Keep me signed in on this device
            </label>
            <Link to="/forgot-password" className="touch-target text-sm font-medium text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
          <p id="remember-hint" className="mt-0.5 pl-6 text-xs text-muted-foreground">
            Leave this off on a shared computer.
          </p>
        </div>

        {error && (
          <AuthNotice
            tone="error"
            id={errId}
            action={
              <button type="button" onClick={() => setError(null)} aria-label="Dismiss error" className="touch-target opacity-70 hover:opacity-100">
                <X className="w-4 h-4" aria-hidden />
              </button>
            }
          >
            {error === "Invalid email or password" ? "Incorrect email or password. Please try again." : error}
          </AuthNotice>
        )}

        <Button type="submit" disabled={busy} className="w-full h-11 text-sm">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />Signing in…</> : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
